import type { IReprintOrderCmd } from "@/data/commands/print/ReprintOrderCmd";
import { Factory } from "@/data/dao/Factory";
import { CatalogDao } from "@/data/dao/service/CatalogDao";
import { PrintDao } from "@/data/dao/service/PrintDao";
import type { IProductionSite } from "@/data/model/ProductionSite";

import { BaseModal } from "@/shared/elements/BaseModal";
import { logError } from "@/shared/logging/errors";
import { style } from "@/shared/ui/Style";

import { i18n } from "@/portal/Trans";
import { Select } from "@/portal/formControls/Select";
import { UiNotification } from "@/portal/ui/Notification";

export class ReprintOrderModal extends BaseModal {
    private $form: JQuery;
    private $submitBtn: JQuery;

    private orderId: string;
    private productionSiteSelect: Select<IProductionSite>;

    constructor(orderId: string, productionSite?: string) {
        super(i18n("modal.reprintOrder.title"), "modal-lg", true);

        this.orderId = orderId;

        this.$form = $('<form class="kt-form kt-form--label-right" />').appendTo(this.$body);

        const settingsSection = $('<div class="kt-section kt-section--first"><div class="kt-section__body" /></div>')
            .appendTo(this.$form)
            .find("> .kt-section__body");
        settingsSection.append(
            '<div class="row"><label class="col-xl-4"></label><div class="col-lg-9 col-xl-6"><h3 class="kt-section__title kt-section__title-sm">' +
                i18n("modal.reprintOrder.settings") +
                "</h3></div></div>"
        );
        const productionSitesGroup = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                i18n("general.entities.productionSites") +
                ' <span class="mandatory">*</span></label><div class="col-xl-6" /></div>'
        )
            .appendTo(settingsSection)
            .find("> .col-xl-6");
        this.productionSiteSelect = new Select<IProductionSite>(
            undefined,
            Factory.getInstance<CatalogDao>(CatalogDao).productionSiteDataSource,
            (c) => c.name,
            false,
            undefined,
            undefined,
            true
        ).renderTo(productionSitesGroup);

        this.productionSiteSelect.setValues(productionSite !== undefined ? [productionSite] : []);

        productionSitesGroup.append(
            '<span class="form-text text-muted">' + i18n("modal.reprintOrder.hint") + "</span>"
        );

        $('<button type="button" class="btn btn-secondary">' + i18n("general.cancel") + "</button>")
            .appendTo(this.$footer!)
            .click((e) => {
                e.preventDefault();
                this.close();
            });

        this.$submitBtn = $(
            '<button type="button" class="btn btn-success">' + i18n("modal.reprintOrder.printBtn") + "</button>"
        )
            .appendTo(this.$footer!)
            .click((e) => {
                e.preventDefault();
                this.$form.submit();
            });

        this.$form.submit((e) => {
            e.preventDefault();
            e.stopPropagation();

            this.onSubmit();
            return false;
        });
    }

    private onSubmit() {
        const cmd: IReprintOrderCmd = {
            productionSiteIds: this.productionSiteSelect.getValues(),
        };

        let hasError = false;
        this.$form.find(".is-invalid").removeClass("is-invalid");

        if (cmd.productionSiteIds.length === 0) {
            hasError = true;
            this.productionSiteSelect.setError(true);
        } else this.productionSiteSelect.setError(false);

        if (hasError) return;

        this.$submitBtn.addClass(style.buttonLoaderClass).prop("disabled", true).find("> .la").remove();
        Factory.getInstance<PrintDao>(PrintDao)
            .reprintOrder(this.orderId, cmd)
            .then(() => {
                this.close();
                UiNotification.showSuccess(i18n("modal.reprintOrder.success"));
            })
            .catch((ex) => {
                logError(ex);
                this.$submitBtn.removeClass(style.buttonLoaderClass).prop("disabled", false);
                UiNotification.showException(i18n("modal.reprintOrder.failedSending"));
            });
    }
}
